import { useEffect, useMemo, type ReactElement } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { LayoutGrid, Settings, ShieldCheck } from "lucide-react";
import { PRODUCT_NAME } from "@playbookai/types";
import { AppLauncher, EmptyState } from "@playbookai/ui";
import { ADMIN_TAB_ID, adminTab } from "../admin/admin-tab.js";
import { useAuthStore } from "../auth/auth-store.js";
import { canAdministerWorkspace } from "../auth/roles.js";
import { getEnabledModules } from "../modules/index.js";
import { NoModulesEmptyState } from "../modules/NoModulesEmptyState.js";
import {
  filterModulesForVisibility,
  isModuleHomeTab,
  manifestToTab,
  usesInstanceSegment,
  manifestsToLauncherEntries,
  orderFavouritesFirst,
} from "../modules/launcher-entries.js";
import { useLauncherPreferencesQuery } from "../settings/useSettings.js";
import { normalizeLauncherModuleId } from "../settings/roster.js";
import { SETTINGS_TAB_ID, settingsTab } from "../settings/settings-tab.js";
import { useTabs, routeForTab } from "../store/tabs.js";
import { ADMIN_ROUTE, SETTINGS_ROUTE } from "../routes.js";

const SETTINGS_ENTRY = {
  id: "settings",
  name: "Settings",
  short: "PREFS",
  blurb: "Manage Player AI, notifications, KPI, and personal preferences.",
  icon: Settings,
  accent: "text-pb-text-secondary",
} as const;

const ADMIN_ENTRY = {
  id: "admin",
  name: "Admin console",
  short: "ADMIN",
  blurb: "Manage workspace members, access, integrations, and governance.",
  icon: ShieldCheck,
  accent: "text-pb-text-secondary",
} as const;

const MEMBER_UTILITY_ENTRIES = [SETTINGS_ENTRY] as const;
const ADMIN_UTILITY_ENTRIES = [SETTINGS_ENTRY, ADMIN_ENTRY] as const;

/**
 * Renders inside the ephemeral "New tab" tab spawned by the TopBar's `+`
 * button. The route is `/launcher/:tabId`; the `tabId` segment is the suffix
 * of the launcher tab's id (`launcher-<tabId>`). Picking an app calls
 * `replaceTab` so the same pill morphs into the chosen module's tab in place.
 *
 * Direct nav to `/launcher` (without a tabId) is handled too: we mint a fresh
 * id, open a launcher tab for it, and rewrite the URL — same end state as
 * pressing `+`. AppLayout's bootstrap would also have caught this, but doing
 * it explicitly avoids a one-frame "no matching tab" flicker.
 */
/** A stable identity while the preference loads, so the memo below caches. */
const NO_FAVOURITES: readonly string[] = [];

export function Launcher(): ReactElement {
  const navigate = useNavigate();
  const { tabId } = useParams<{ tabId: string }>();
  const openTab = useTabs((s) => s.openTab);
  const replaceTab = useTabs((s) => s.replaceTab);
  const claims = useAuthStore((state) => state.claims);
  const tenantEnabledModules = claims?.enabledModules;
  const canAdminister = canAdministerWorkspace(claims);

  const modules = useMemo(
    () => getEnabledModules(tenantEnabledModules),
    [tenantEnabledModules],
  );

  // The "Show more apps" launcher pref gates the grid (a discovery surface);
  // default compact until it loads, matching the seed. The unfiltered `modules`
  // list still backs click-resolution, so a hidden module reached another way
  // would still open.
  const { data: launcherPrefs } = useLauncherPreferencesQuery();
  const showMoreApps = launcherPrefs?.showMoreApps ?? false;
  const rawFavourites = launcherPrefs?.favourites ?? NO_FAVOURITES;
  const favourites = useMemo(
    () => rawFavourites.map(normalizeLauncherModuleId),
    [rawFavourites],
  );
  const hasCuratedModules = launcherPrefs?.moduleRosterConfigured === true;
  const entries = useMemo(() => {
    const candidates = hasCuratedModules
      ? modules
      : filterModulesForVisibility(modules, showMoreApps);
    const ordered = orderFavouritesFirst(candidates, favourites);
    const visible = hasCuratedModules
      ? ordered.filter((module) => favourites.includes(module.id))
      : ordered;
    return manifestsToLauncherEntries(visible);
  }, [modules, showMoreApps, favourites, hasCuratedModules]);

  // Cold-entry from a direct URL like `/launcher`: synth a launcher tab.
  useEffect(() => {
    if (tabId) return;
    const fresh = String(useTabs.getState().nextCount("launcher"));
    openTab({
      id: `launcher-${fresh}`,
      label: "New tab",
      route: `/launcher/${fresh}`,
    });
    navigate(`/launcher/${fresh}`, { replace: true });
  }, [tabId, openTab, navigate]);

  return (
    <div className="pb-motion-content-enter mx-auto max-w-6xl px-6 py-8">
      {/* Kept hand-built: SectionHeader spaces this pair 4px and 24px (today
          8px and 32px) and caps the line at max-w-xl, a look change no PR 3
          row approves. */}
      <h1 className="mb-2 text-heading-1 text-pb-text">App launcher</h1>
      <p className="mb-8 text-sm text-pb-text-secondary">
        Pick any module that&rsquo;s enabled for this workspace.
      </p>
      <AppLauncher
        heading={`${PRODUCT_NAME} apps`}
        apps={entries}
        emptyState={
          modules.length === 0 ? (
            <NoModulesEmptyState />
          ) : hasCuratedModules ? (
            <EmptyState
              variant="first-use"
              icon={<LayoutGrid className="h-5 w-5" />}
              title="No modules in your launcher"
              description="Open Settings, then Launcher, to add the modules you use."
            />
          ) : undefined
        }
        utilitySection={{
          entries: canAdminister
            ? ADMIN_UTILITY_ENTRIES
            : MEMBER_UTILITY_ENTRIES,
          onItemClick: (entry) => {
            if (!tabId) return;
            const state = useTabs.getState();
            if (entry.id === ADMIN_ENTRY.id) {
              const existing = state.tabs.find(
                (tab) => tab.id === ADMIN_TAB_ID,
              );
              if (existing) {
                state.forceCloseTab(`launcher-${tabId}`);
                state.openTab(existing);
                navigate(routeForTab(existing.id) ?? existing.route);
                return;
              }
              replaceTab(`launcher-${tabId}`, adminTab());
              navigate(ADMIN_ROUTE);
              return;
            }
            const existing = state.tabs.find(
              (tab) => tab.id === SETTINGS_TAB_ID,
            );
            if (existing) {
              state.forceCloseTab(`launcher-${tabId}`);
              state.openTab(existing);
              navigate(routeForTab(existing.id) ?? existing.route);
              return;
            }
            replaceTab(`launcher-${tabId}`, settingsTab());
            navigate(SETTINGS_ROUTE);
          },
        }}
        onAppClick={(entry) => {
          const module = modules.find((m) => m.id === entry.id);
          if (!module || !tabId) return;
          const state = useTabs.getState();
          // Open-or-focus, the same contract the Settings entry above honours:
          // one home tab per module. `find` takes the leftmost when more than
          // one somehow exists. Entity tabs are excluded by route shape, so a
          // pick never hijacks an open workspace.
          const existing = state.tabs.find((tab) =>
            isModuleHomeTab(tab, module),
          );
          if (existing) {
            state.forceCloseTab(`launcher-${tabId}`);
            state.openTab(existing);
            navigate(routeForTab(existing.id) ?? existing.route);
            return;
          }
          // Only an `instanceSegment` module consumes a minted id; minting for
          // the others would bump a counter whose value manifestToTab discards.
          const instanceId = usesInstanceSegment(module)
            ? String(state.nextCount(module.id))
            : "";
          const tab = manifestToTab(module, instanceId);
          replaceTab(`launcher-${tabId}`, tab);
          navigate(tab.route);
        }}
      />
    </div>
  );
}
